import { messageCatalogs, type AppLocale, type MessageTree } from "@airange/shared/i18n-redblue-admin";
import routeConfig from "../app/routes";
import { getRedblueMessages } from "./catalog";

const menuMessageKeyAliases: Record<string, string> = {
  redblueMembers: "redblue.menu.members",
  redblueSchemes: "redblue.menu.schemes",
  redblueSchemeWorkbench: "redblue.pages.schemeWorkbench.title",
  redblueSchemeDetail: "redblue.pages.schemeDetail.title",
  redblueActivities: "redblue.menu.activities",
  redblueActivityCreate: "redblue.pages.activityCreate.title",
  redblueActivityDetail: "redblue.pages.activityDetail.title",
  redblueDirector: "redblue.menu.director",
  redblueJudge: "redblue.menu.judge",
  redblueReview: "redblue.menu.review",
  redblueAcceptance: "redblue.menu.acceptance",
};

type RouteLocaleNode = {
  name?: string;
  routes?: RouteLocaleNode[];
};

function buildNestedMenuMessageKeyAliases(
  routes: readonly RouteLocaleNode[],
  parentNames: string[] = [],
): Record<string, string> {
  return routes.reduce<Record<string, string>>((result, route) => {
    const nextParentNames = route.name ? [...parentNames, route.name] : parentNames;
    if (route.name) {
      const sourceKey = menuMessageKeyAliases[route.name];
      if (sourceKey) {
        result[`menu.${nextParentNames.join(".")}`] = sourceKey;
      }
    }
    if (route.routes?.length) {
      Object.assign(result, buildNestedMenuMessageKeyAliases(route.routes, nextParentNames));
    }
    return result;
  }, {});
}

const menuMessageKeys = {
  ...Object.fromEntries(
    Object.entries(menuMessageKeyAliases).map(([menuKey, sourceKey]) => [
      `menu.${menuKey}`,
      sourceKey,
    ]),
  ),
  ...buildNestedMenuMessageKeyAliases(routeConfig as RouteLocaleNode[]),
} as const;

function getNestedValue(tree: MessageTree | undefined, path: string) {
  if (!tree) return undefined;
  return path.split(".").reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, tree);
}

function applyMessageAliases(messages: Record<string, string>, source: MessageTree) {
  Object.entries(menuMessageKeys).forEach(([targetKey, sourceKey]) => {
    const resolved = getNestedValue(source, sourceKey);
    if (typeof resolved === "string") {
      messages[targetKey] = resolved;
    }
  });
}

function flattenMessageTree(tree: MessageTree, prefix = ""): Record<string, string> {
  return Object.entries(tree).reduce<Record<string, string>>((result, [key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      result[nextKey] = value;
      return result;
    }
    if (value && typeof value === "object") {
      Object.assign(result, flattenMessageTree(value as MessageTree, nextKey));
    }
    return result;
  }, {});
}

export function buildUmiLocaleMessages(locale: AppLocale) {
  const sharedMessages = messageCatalogs[locale] ?? {};
  const appTree = getRedblueMessages(locale);
  const merged = {
    ...sharedMessages,
    ...appTree,
  };
  const flatMessages = flattenMessageTree(merged);
  applyMessageAliases(flatMessages, merged);
  return flatMessages;
}
